import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  TrialBalanceParams,
  TrialBalanceReport,
  TrialBalanceResponse,
  StockValuationParams,
  StockValuationReport,
  StockValuationResponse,
  BankCollateralStockParams,
  BankCollateralStockReport,
  BankCollateralStockResponse,
  ProfitAndLossParams,
  ProfitAndLossReport,
  ProfitAndLossResponse,
  ProfitAndLossDetailParams,
  ProfitAndLossDetailReport,
  ProfitAndLossDetailResponse,
  ArAgingParams,
  ArAgingReport,
  ArAgingResponse,
  ApAgingParams,
  ApAgingReport,
  ApAgingResponse,
  BalanceSheetParams,
  BalanceSheetReport,
  BalanceSheetResponse,
  CustomerBalanceSummaryParams,
  CustomerBalanceSummaryReport,
  CustomerBalanceSummaryResponse,
  CustomerBalanceDetailParams,
  CustomerBalanceDetailReport,
  CustomerBalanceDetailResponse,
  VendorBalanceSummaryParams,
  VendorBalanceSummaryReport,
  VendorBalanceSummaryResponse,
  VendorBalanceDetailParams,
  VendorBalanceDetailReport,
  VendorBalanceDetailResponse,
  SalesByCustomerSummaryParams,
  SalesByCustomerSummaryReport,
  SalesByCustomerSummaryResponse,
  SalesByCustomerDetailParams,
  SalesByCustomerDetailReport,
  SalesByCustomerDetailResponse,
  SalesByProductSummaryParams,
  SalesByProductSummaryReport,
  SalesByProductSummaryResponse,
  SalesByProductDetailParams,
  SalesByProductDetailReport,
  SalesByProductDetailResponse,
  IncomeByCustomerSummaryParams,
  IncomeByCustomerSummaryReport,
  IncomeByCustomerSummaryResponse,
  IncomeByCustomerDetailParams,
  IncomeByCustomerDetailReport,
  IncomeByCustomerDetailResponse,
  IncomeByCustomerInvoiceDetailParams,
  IncomeByCustomerInvoiceDetailReport,
  IncomeByCustomerInvoiceDetailResponse,
} from '../types/report';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getTrialBalance = async (
  params?: TrialBalanceParams
): Promise<TrialBalanceReport> => {
  const queryParams = new URLSearchParams();

  if (params?.as_of) {
    queryParams.set('as_of', params.as_of);
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.TRIAL_BALANCE}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch trial balance');
  }

  const result: TrialBalanceResponse = await response.json();
  return result.data;
};

export const getStockValuation = async (
  params?: StockValuationParams
): Promise<StockValuationReport> => {
  const queryParams = new URLSearchParams();

  if (params?.item_id) {
    queryParams.set('item_id', String(params.item_id));
  }
  if (params?.include_layers) {
    queryParams.set('include_layers', '1');
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.STOCK_VALUE}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch stock valuation');
  }

  const result: StockValuationResponse = await response.json();
  return result.data;
};

export const getBankCollateralStock = async (
  params?: BankCollateralStockParams
): Promise<BankCollateralStockReport> => {
  const queryParams = new URLSearchParams();

  if (params?.item_id) {
    queryParams.set('item_id', String(params.item_id));
  }
  if (params?.active_only === false) {
    queryParams.set('active_only', '0');
  }
  if (params?.include_zero_qty) {
    queryParams.set('include_zero_qty', '1');
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.BANK_COLLATERAL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch bank collateral stock report');
  }

  const result: BankCollateralStockResponse = await response.json();
  return result.data;
};

export const getProfitAndLoss = async (
  params?: ProfitAndLossParams
): Promise<ProfitAndLossReport> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.include_zero_balances) {
    queryParams.set('include_zero_balances', '1');
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.PROFIT_AND_LOSS}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch profit and loss report');
  }

  const result: ProfitAndLossResponse = await response.json();
  return result.data;
};

export const getProfitAndLossDetail = async (
  params?: ProfitAndLossDetailParams
): Promise<ProfitAndLossDetailReport> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.from_inception) {
    queryParams.set('from_inception', '1');
  }
  if (params?.account_id) {
    queryParams.set('account_id', String(params.account_id));
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.PROFIT_AND_LOSS_DETAIL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch profit and loss detail');
  }

  const result: ProfitAndLossDetailResponse = await response.json();
  return result.data;
};

export const getBalanceSheet = async (
  params?: BalanceSheetParams
): Promise<BalanceSheetReport> => {
  const queryParams = new URLSearchParams();

  if (params?.as_of) {
    queryParams.set('as_of', params.as_of);
  }
  if (params?.include_zero_balances) {
    queryParams.set('include_zero_balances', '1');
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.BALANCE_SHEET}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch balance sheet');
  }

  const result: BalanceSheetResponse = await response.json();
  return result.data;
};

export const getArAging = async (params?: ArAgingParams): Promise<ArAgingReport> => {
  const queryParams = new URLSearchParams();

  if (params?.as_of) {
    queryParams.set('as_of', params.as_of);
  }
  if (params?.customer_id) {
    queryParams.set('customer_id', String(params.customer_id));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.AR_AGING}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch AR aging report');
  }

  const result: ArAgingResponse = await response.json();
  return result.data;
};

export const getApAging = async (params?: ApAgingParams): Promise<ApAgingReport> => {
  const queryParams = new URLSearchParams();

  if (params?.as_of) {
    queryParams.set('as_of', params.as_of);
  }
  if (params?.vendor_id) {
    queryParams.set('vendor_id', String(params.vendor_id));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.AP_AGING}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch AP aging report');
  }

  const result: ApAgingResponse = await response.json();
  return result.data;
};

export const getCustomerBalanceSummary = async (
  params?: CustomerBalanceSummaryParams
): Promise<CustomerBalanceSummaryReport> => {
  const queryParams = new URLSearchParams();

  if (params?.as_of) {
    queryParams.set('as_of', params.as_of);
  }
  if (params?.include_zero_balances) {
    queryParams.set('include_zero_balances', 'true');
  }
  if (params?.customer_id) {
    queryParams.set('customer_id', String(params.customer_id));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.CUSTOMER_BALANCE_SUMMARY}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch customer balance summary');
  }

  const result: CustomerBalanceSummaryResponse = await response.json();
  return result.data;
};

export const getCustomerBalanceDetail = async (
  params?: CustomerBalanceDetailParams
): Promise<CustomerBalanceDetailReport> => {
  const queryParams = new URLSearchParams();

  if (params?.as_of) {
    queryParams.set('as_of', params.as_of);
  }
  if (params?.customer_id) {
    queryParams.set('customer_id', String(params.customer_id));
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.CUSTOMER_BALANCE_DETAIL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch customer balance detail');
  }

  const result: CustomerBalanceDetailResponse = await response.json();
  return result.data;
};

export const getVendorBalanceSummary = async (
  params?: VendorBalanceSummaryParams
): Promise<VendorBalanceSummaryReport> => {
  const queryParams = new URLSearchParams();

  if (params?.as_of) {
    queryParams.set('as_of', params.as_of);
  }
  if (params?.include_zero_balances) {
    queryParams.set('include_zero_balances', 'true');
  }
  if (params?.vendor_id) {
    queryParams.set('vendor_id', String(params.vendor_id));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.VENDOR_BALANCE_SUMMARY}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch vendor balance summary');
  }

  const result: VendorBalanceSummaryResponse = await response.json();
  return result.data;
};

export const getVendorBalanceDetail = async (
  params?: VendorBalanceDetailParams
): Promise<VendorBalanceDetailReport> => {
  const queryParams = new URLSearchParams();

  if (params?.as_of) {
    queryParams.set('as_of', params.as_of);
  }
  if (params?.vendor_id) {
    queryParams.set('vendor_id', String(params.vendor_id));
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.VENDOR_BALANCE_DETAIL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch vendor balance detail');
  }

  const result: VendorBalanceDetailResponse = await response.json();
  return result.data;
};

export const getSalesByCustomerSummary = async (
  params?: SalesByCustomerSummaryParams
): Promise<SalesByCustomerSummaryReport> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.customer_id) {
    queryParams.set('customer_id', String(params.customer_id));
  }
  if (params?.include_zero_balances) {
    queryParams.set('include_zero_balances', 'true');
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.SALES_BY_CUSTOMER_SUMMARY}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch sales by customer summary');
  }

  const result: SalesByCustomerSummaryResponse = await response.json();
  return result.data;
};

export const getSalesByCustomerDetail = async (
  params?: SalesByCustomerDetailParams
): Promise<SalesByCustomerDetailReport> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.customer_id) {
    queryParams.set('customer_id', String(params.customer_id));
  }
  if (params?.include_lines) {
    queryParams.set('include_lines', 'true');
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.SALES_BY_CUSTOMER_DETAIL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch sales by customer detail');
  }

  const result: SalesByCustomerDetailResponse = await response.json();
  return result.data;
};

export const getSalesByProductSummary = async (
  params?: SalesByProductSummaryParams
): Promise<SalesByProductSummaryReport> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.item_id) {
    queryParams.set('item_id', String(params.item_id));
  }
  if (params?.include_zero_balances) {
    queryParams.set('include_zero_balances', 'true');
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.SALES_BY_PRODUCT_SUMMARY}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch sales by product summary');
  }

  const result: SalesByProductSummaryResponse = await response.json();
  return result.data;
};

export const getSalesByProductDetail = async (
  params?: SalesByProductDetailParams
): Promise<SalesByProductDetailReport> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.item_id) {
    queryParams.set('item_id', String(params.item_id));
  }
  if (params?.description) {
    queryParams.set('description', params.description);
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.SALES_BY_PRODUCT_DETAIL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch sales by product detail');
  }

  const result: SalesByProductDetailResponse = await response.json();
  return result.data;
};

export const getIncomeByCustomerSummary = async (
  params?: IncomeByCustomerSummaryParams
): Promise<IncomeByCustomerSummaryReport> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.customer_id) {
    queryParams.set('customer_id', String(params.customer_id));
  }
  if (params?.include_zero_balances) {
    queryParams.set('include_zero_balances', 'true');
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.INCOME_BY_CUSTOMER_SUMMARY}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch income by customer summary');
  }

  const result: IncomeByCustomerSummaryResponse = await response.json();
  return result.data;
};

export const getIncomeByCustomerDetail = async (
  params?: IncomeByCustomerDetailParams
): Promise<IncomeByCustomerDetailReport> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.customer_id) {
    queryParams.set('customer_id', String(params.customer_id));
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.INCOME_BY_CUSTOMER_DETAIL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch income by customer detail');
  }

  const result: IncomeByCustomerDetailResponse = await response.json();
  return result.data;
};

export const getIncomeByCustomerInvoiceDetail = async (
  params: IncomeByCustomerInvoiceDetailParams
): Promise<IncomeByCustomerInvoiceDetailReport> => {
  const queryParams = new URLSearchParams();
  queryParams.set('invoice_id', String(params.invoice_id));

  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.INCOME_BY_CUSTOMER_INVOICE_DETAIL}?${queryParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch income by customer invoice detail');
  }

  const result: IncomeByCustomerInvoiceDetailResponse = await response.json();
  return result.data;
};
